import { useContext, useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { ShieldCheck, LayoutDashboard, FileCheck, Activity, AlertTriangle, AlertOctagon, LogOut, Menu, X, Cloud, FileText, FolderOpen, Users, Server, ChevronDown, ChevronRight, Shield, Zap, Building2, Crown, ArrowLeft, User, ClipboardList, UserCheck, Library, ShieldAlert, FileSignature, BarChart3, FileDown, ListChecks } from 'lucide-react';
import MFANotificationBanner from './MFANotificationBanner';
import PolicyAcknowledgementBanner from './PolicyAcknowledgementBanner';
import axios from 'axios';
import { API } from '../App';

export default function Layout() {
  const { user, logout, viewingOrg, selectOrg, enabledFrameworks } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [integrationsOpen, setIntegrationsOpen] = useState(true);
  // Persist Framework dropdown state across reloads. Default: open, so enabled
  // frameworks are visible without an extra click.
  const [frameworkOpen, setFrameworkOpen] = useState(() => {
    try {
      const v = localStorage.getItem('cf_sidebar_framework_open');
      return v === null ? true : v === 'true';
    } catch { return true; }
  });
  const [iso27001Open, setIso27001Open] = useState(true);
  const [mfaStatus, setMfaStatus] = useState(null);
  
  const isSuperAdmin = user?.role === 'super_admin';
  const isClient = user?.role === 'client' || user?.role === 'auditor';

  useEffect(() => {
    // Fetch MFA status for the notification banner
    const fetchMFAStatus = async () => {
      try {
        const response = await axios.get(`${API}/mfa/status`);
        setMfaStatus(response.data);
      } catch (error) {
        console.error('Failed to fetch MFA status:', error);
      }
    };
    fetchMFAStatus();
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleBackToOverview = () => {
    selectOrg(null);
    navigate('/');
  };

  // Map framework IDs to their nav items
  // ISO 27001 and SOC 2 now have dedicated pages derived from CyberForte
  const frameworkNavMap = {
    'ISO27001': { path: '/iso27001', label: 'ISO 27001', icon: Shield },
    'SOC2': { path: '/soc2', label: 'SOC 2', icon: ShieldCheck },
    'Essential8': { path: '/essential8', label: 'Essential 8', icon: Shield },
    'PCIDSS': { path: '/pci-dss', label: 'PCI DSS', icon: ShieldCheck },
    'HIPAA': { path: '/hipaa', label: 'HIPAA', icon: Shield },
    'ISO42001': { path: '/iso42001', label: 'ISO 42001', icon: Shield },
    'SOCI': { path: '/soci', label: 'SOCI', icon: Shield },
  };

  // Framework menu strictly follows the tenant's Organization Settings —
  // if the admin only enabled ISO 27001, everyone (super_admin, admin,
  // client) sees only ISO 27001 in the Framework menu and SoA framework
  // picker. Admins can still enable/disable frameworks in Organization →
  // Compliance Frameworks to change what appears here.

  // Filter framework children based on enabled frameworks only.
  const getEnabledFrameworkItems = () => {
    const enabledSet = new Set(enabledFrameworks || []);
    return Object.keys(frameworkNavMap)
      .filter((fwId) => enabledSet.has(fwId))
      .map((fwId) => frameworkNavMap[fwId]);
  };

  const navItems = [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/improvement-actions', label: 'Improvement & Actions', icon: ListChecks },
    // Master Control Library is admin-only; clients see the derived
    // Framework / SoA / Gap views instead.
    ...(isClient ? [] : [{ path: '/master-control-library', label: 'Master Control Library', icon: Library }]),
    { 
      label: 'Framework', 
      icon: FileCheck,
      isParent: true,
      children: getEnabledFrameworkItems()
    },
    { path: '/gap-engine', label: 'Gap Assessment', icon: ShieldAlert },
    { path: '/soa', label: 'Statement of Applicability', icon: FileSignature },
    { path: '/risk-engine', label: 'Risk Register', icon: AlertOctagon },
    ...(isClient ? [] : [
      { path: '/evidence-engine', label: 'Evidence Engine', icon: ClipboardList },
      { path: '/management-review', label: 'Management Review', icon: BarChart3 },
    ]),
    { path: '/reports', label: 'Reports Hub', icon: FileDown },
    { path: '/policies', label: 'Policies & Procedures', icon: FileText },
    { path: '/policy-acknowledgement', label: 'Policy Acknowledgement', icon: UserCheck },
    { path: '/documents', label: 'Document Repository', icon: FolderOpen },
    { path: '/incident-register', label: 'Incident Register', icon: AlertOctagon },
    { path: '/asset-register', label: 'Asset Register', icon: Server },
    { path: '/supplier-register', label: 'Supplier Register', icon: Users },
    ...(isClient ? [] : [{ path: '/threat-intelligence', label: 'Threat Intelligence', icon: Zap }]),
    ...(isSuperAdmin ? [{
      label: 'Integrations',
      icon: Cloud,
      isParent: true,
      children: [
        { path: '/m365-integration', label: 'Microsoft 365', icon: Cloud },
        { path: '/azure-integration', label: 'Azure', icon: Cloud },
        { path: '/aws-integration', label: 'AWS', icon: Server },
        { path: '/gcp-integration', label: 'GCP', icon: Cloud },
      ]
    }] : []),
    ...(isSuperAdmin ? [{ path: '/audit-log', label: 'Audit Log', icon: Activity }] : []),
    { path: '/trust-center', label: 'Trust Center', icon: Shield },
    ...(isClient ? [] : [
      { path: '/user-lifecycle', label: 'User Lifecycle', icon: Users },
      { path: '/organization-settings', label: 'Organization', icon: Building2 },
    ]),
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-slate-900 text-white z-50 border-b border-slate-800">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-6 h-6" />
            <div>
              <h1 className="text-lg font-heading font-bold">Cyber Forte</h1>
              <p className="text-xs text-slate-600">Compliance Manager</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-white"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40 mt-[73px]"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 w-64 bg-slate-900 text-white z-40 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
        mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:block mt-[73px] lg:mt-0`}>
        <div className="flex flex-col h-full">
          {/* Logo - Desktop Only */}
          <div className="hidden lg:block p-6 border-b border-slate-800">
            <div className="flex items-center gap-3">
              <ShieldCheck className="w-8 h-8" />
              <div>
                <h1 className="text-xl font-heading font-bold tracking-tight">Cyber Forte</h1>
                <p className="text-xs text-slate-600">Compliance Manager</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 overflow-y-auto" data-testid="sidebar-nav">
            <ul className="space-y-2">
              {navItems.map((item, index) => {
                const Icon = item.icon;
                
                // Parent menu item with children
                if (item.isParent && item.children) {
                  const isAnyChildActive = item.children.some(child => 
                    location.pathname === child.path || 
                    (child.path !== '/' && location.pathname.startsWith(child.path))
                  );
                  
                  return (
                    <li key={item.label}>
                      {/* Parent menu item */}
                      <div className="relative">
                        <Link
                          to={item.label === 'Framework' ? '/framework' : '#'}
                          onClick={(e) => {
                            if (item.label !== 'Framework') {
                              e.preventDefault();
                            }
                          }}
                          className={`w-full flex items-center justify-between px-4 py-3 rounded-sm transition-colors ${
                            (isAnyChildActive || location.pathname === '/framework')
                              ? 'bg-slate-800 text-white'
                              : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <Icon className="w-5 h-5" />
                            <span className="font-medium">{item.label}</span>
                          </div>
                        </Link>
                        <button
                          onClick={() => {
                            if (item.label === 'Framework') {
                              const next = !frameworkOpen;
                              setFrameworkOpen(next);
                              try { localStorage.setItem('cf_sidebar_framework_open', String(next)); } catch {}
                            } else if (item.label === 'Integrations') {
                              setIntegrationsOpen(!integrationsOpen);
                            }
                          }}
                          className="absolute right-4 top-3 text-slate-300 hover:text-white"
                        >
                          {((item.label === 'Framework' && frameworkOpen) || (item.label === 'Integrations' && integrationsOpen)) ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                      
                      {/* Children menu items */}
                      {((item.label === 'Framework' && frameworkOpen) || (item.label === 'Integrations' && integrationsOpen)) && (
                        <ul className="mt-1 ml-4 space-y-1">
                          {item.children.map((child) => {
                            const ChildIcon = child.icon;
                            
                            // Handle nested parent (like ISO 27001)
                            if (child.isParent) {
                              const isAnyNestedChildActive = child.children?.some(nc => location.pathname === nc.path);
                              
                              return (
                                <li key={child.label} className="space-y-1">
                                  <button
                                    onClick={() => {
                                      if (child.label === 'ISO 27001') {
                                        setIso27001Open(!iso27001Open);
                                      }
                                    }}
                                    className={`w-full flex items-center justify-between px-4 py-2 rounded-sm transition-colors ${
                                      isAnyNestedChildActive
                                        ? 'bg-slate-700 text-white'
                                        : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'
                                    }`}
                                  >
                                    <div className="flex items-center gap-3">
                                      <ChildIcon className="w-4 h-4" />
                                      <span className="text-sm font-medium">{child.label}</span>
                                    </div>
                                    {iso27001Open ? (
                                      <ChevronDown className="w-3 h-3" />
                                    ) : (
                                      <ChevronRight className="w-3 h-3" />
                                    )}
                                  </button>
                                  
                                  {/* Nested children */}
                                  {iso27001Open && (
                                    <ul className="ml-6 space-y-1">
                                      {child.children.map((nestedChild) => {
                                        const NestedIcon = nestedChild.icon;
                                        const isNestedActive = location.pathname === nestedChild.path;
                                        
                                        return (
                                          <li key={nestedChild.path}>
                                            <Link
                                              to={nestedChild.path}
                                              onClick={() => setMobileMenuOpen(false)}
                                              className={`flex items-center gap-2 px-3 py-1.5 rounded-sm transition-colors ${
                                                isNestedActive
                                                  ? 'bg-slate-700 text-white'
                                                  : 'text-slate-500 hover:bg-slate-800/50 hover:text-white'
                                              }`}
                                            >
                                              <NestedIcon className="w-3 h-3" />
                                              <span className="text-xs font-medium">{nestedChild.label}</span>
                                            </Link>
                                          </li>
                                        );
                                      })}
                                    </ul>
                                  )}
                                </li>
                              );
                            }
                            
                            // Regular child item
                            const isActive = location.pathname === child.path;
                            
                            return (
                              <li key={child.path}>
                                <Link
                                  to={child.path}
                                  onClick={() => setMobileMenuOpen(false)}
                                  className={`flex items-center gap-3 px-4 py-2 rounded-sm transition-colors ${
                                    isActive
                                      ? 'bg-slate-700 text-white'
                                      : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'
                                  }`}
                                >
                                  <ChildIcon className="w-4 h-4" />
                                  <span className="text-sm font-medium">{child.label}</span>
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                }
                
                // Regular menu item
                const isActive = location.pathname === item.path || 
                  (item.path !== '/' && location.pathname.startsWith(item.path));
                
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                        item.highlight
                          ? isActive
                            ? 'bg-purple-700 text-white'
                            : 'bg-purple-900/50 text-purple-200 hover:bg-purple-800 hover:text-white border border-purple-700'
                          : isActive
                            ? 'bg-slate-800 text-white'
                            : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'
                      }`}
                    >
                      <Icon className={`w-5 h-5 ${item.highlight ? 'text-purple-300' : ''}`} />
                      <span className="font-medium">{item.label}</span>
                      {item.highlight && (
                        <span className="ml-auto text-xs bg-purple-600 text-white px-1.5 py-0.5 rounded">NEW</span>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Tenant branding badge (name + logo) */}
          <TenantBadge viewingOrg={viewingOrg} userTenantName={user?.tenant_name} />

          {/* User Info */}
          <div className="p-4 border-t border-slate-800">
            <div className="flex items-center justify-between mb-3">
              <div className="flex-1 min-w-0">
                {viewingOrg ? (
                  <p className="text-xs text-purple-400 font-medium mb-1 truncate" title={viewingOrg.name}>
                    Viewing: {viewingOrg.name}
                  </p>
                ) : user?.tenant_name && (
                  <p className="text-xs text-slate-700 font-medium mb-1 truncate" title={user?.tenant_name}>
                    {user?.tenant_name}
                  </p>
                )}
                <p className="text-sm font-medium truncate" data-testid="user-name">{user?.name}</p>
                <p className="text-xs text-slate-600" data-testid="user-role">{user?.role}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/profile"
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-800 rounded-sm transition-colors"
                data-testid="profile-link"
              >
                <User className="w-4 h-4" />
                Profile
              </Link>
              <Button
                data-testid="logout-button"
                onClick={handleLogout}
                variant="ghost"
                size="sm"
                className="text-slate-300 hover:text-white hover:bg-slate-800"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-[73px] lg:pt-0">
        {/* MFA Notification Banner */}
        <MFANotificationBanner mfaEnabled={mfaStatus?.mfa_enabled} />
        
        {/* Policy Acknowledgement Banner */}
        <PolicyAcknowledgementBanner />
        
        {/* Super Admin Viewing Org Banner */}
        {isSuperAdmin && viewingOrg && (
          <div className="bg-purple-600 text-white px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Crown className="w-5 h-5" />
              <span className="font-medium">
                Viewing: <strong>{viewingOrg.name}</strong>
              </span>
              <span className="text-purple-200 text-sm">
                ({viewingOrg.compliance_stats?.compliance_percentage || 0}% compliant)
              </span>
            </div>
            <Button
              onClick={handleBackToOverview}
              variant="ghost"
              size="sm"
              className="text-white hover:bg-purple-700"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Organizations
            </Button>
          </div>
        )}
        <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}


function TenantBadge({ viewingOrg, userTenantName }) {
  const [branding, setBranding] = useState({ name: null, logo_url: null });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const q = viewingOrg?.id ? `?view_tenant_id=${viewingOrg.id}` : '';
        const res = await axios.get(`${API}/tenant/branding${q}`);
        if (!cancelled) setBranding(res.data || { name: null, logo_url: null });
      } catch {
        if (!cancelled) setBranding({ name: null, logo_url: null });
      }
    };
    load();
    const onUpdate = () => load();
    window.addEventListener('tenant-logo-updated', onUpdate);
    return () => {
      cancelled = true;
      window.removeEventListener('tenant-logo-updated', onUpdate);
    };
  }, [viewingOrg]);

  const displayName = branding.name || viewingOrg?.name || userTenantName;
  if (!displayName) return null;

  return (
    <div
      className="mx-3 mb-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800/60 border border-slate-700/50"
      data-testid="tenant-badge"
    >
      {branding.logo_url ? (
        <img
          src={branding.logo_url}
          alt=""
          className="w-8 h-8 rounded object-contain bg-white p-0.5 shrink-0"
          data-testid="tenant-badge-logo"
        />
      ) : (
        <div className="w-8 h-8 rounded bg-slate-700 flex items-center justify-center text-xs font-semibold text-slate-200 shrink-0" data-testid="tenant-badge-initial">
          {displayName.trim().charAt(0).toUpperCase()}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-wide text-slate-500 leading-none mb-0.5">
          {viewingOrg ? 'Viewing' : 'Tenant'}
        </p>
        <p className="text-sm font-medium text-slate-100 truncate" title={displayName} data-testid="tenant-badge-name">
          {displayName}
        </p>
      </div>
    </div>
  );
}